import { useCallback } from "react"
import type { FC, ReactElement } from "react"

import values from "lodash-es/values"
import { type Params, useParams } from "react-router-dom"
import { useUpdateEffect } from "react-use"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    type TInputProps,
    Typography
} from "@/3514/components"
import { isBadRequestError, isInternalServerError } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyRenderCallback, emptyRenderCallback } from "@/shared/types/functions"

import { type TPassMutation, usePassMutation } from "./auth.api.mutations.hooks"
import { AuthContainer } from "./auth.container"
import {
    type TPostRegisterOrResetFunction,
    useForceNavigateToLogin,
    usePostRegisterOrResetFunction
} from "./auth.ui.hooks"
import type {
    TAuthFormRenderInputsPropsFunction,
    TAuthFormRenderInputsPropsFunctionParams,
    TAuthFormValues
} from "./auth.ui.types"

const renderFormResetHint: TEmptyRenderCallback = emptyRenderCallback

const AuthRegisterPage: FC = (): ReactElement => {
    useForceNavigateToLogin()

    const { t }: IUseTranslation = useTranslation()

    const { key }: Readonly<Params> = useParams<{ key: string }>()

    const postResetHandler: TPostRegisterOrResetFunction = usePostRegisterOrResetFunction()

    const {
        data: passData,
        mutateAsync: passMutator,
        error: passError,
        isSuccess: isPassSuccess,
        isError: isPassError
    }: TPassMutation = usePassMutation({ key })

    useUpdateEffect((): void => {
        if (isPassSuccess && passData) void postResetHandler(passData)
    }, [isPassSuccess, passData, postResetHandler])

    const renderInputsProps: TAuthFormRenderInputsPropsFunction = useCallback(
        (params: TAuthFormRenderInputsPropsFunctionParams): TInputProps[] => {
            const _isBadRequestError: boolean = isBadRequestError(passError)
            const _isInternalServerError: boolean = isInternalServerError(passError)

            const _passErrors: string[] = values(params.errors?.password?.types) as []
            const _confirmPassErrors: string[] = values(params.errors?.confirmPassword?.types) as []

            if (_isBadRequestError) _confirmPassErrors.push(passError.response?.data?.password)
            if (_isInternalServerError) _confirmPassErrors.push(t("auth.registerPage.generalErrorHint"))

            return [
                {
                    type: "email",
                    isRequired: true,
                    placeholder: undefined,
                    isDisabled: true,
                    isError: false,
                    errorMessages: undefined,
                    withSpelling: false,
                    iconType: "lock",
                    mode: "text",
                    ...params.register("email")
                },
                {
                    type: "password",
                    isRequired: true,
                    placeholder: t("auth.passInput.register.placeholder"),
                    isDisabled: params.isSubmitting,
                    enterKeyHint: "done",
                    isError: Object.hasOwn(params.errors, "password") || isPassError,
                    errorMessages: _passErrors,
                    withSpelling: false,
                    mode: "text",
                    autoComplete: "on",
                    maxSymbols: 100,
                    ...params.register("password")
                },
                {
                    type: "password",
                    isRequired: true,
                    placeholder: t("auth.passInput.register.confirm.placeholder"),
                    isDisabled: params.isSubmitting,
                    enterKeyHint: "done",
                    isError: Object.hasOwn(params.errors, "confirmPassword") || isPassError,
                    errorMessages: _confirmPassErrors,
                    withSpelling: false,
                    mode: "text",
                    autoComplete: "on",
                    maxSymbols: 100,
                    ...params.register("confirmPassword")
                }
            ]
        },
        [t, passError, isPassError]
    )

    const handleFormSubmit: (values: TAuthFormValues) => Promise<void> = useCallback(
        async (values: TAuthFormValues): Promise<void> => {
            await passMutator({
                password: values.password,
                password_confirm: values.confirmPassword,
                registration_key: key,
                tz: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
        },
        [passMutator, key]
    )

    function renderSubtitle(): ReactElement {
        return (
            <Typography
                text={t("auth.registerPage.subtitle")}
                color={ETypographyColor.Dark}
                alignment={ETypographyAlignment.Center}
                size={ETypographySize.Small}
            />
        )
    }

    return (
        <AuthContainer
            mode="register"
            renderSubtitle={renderSubtitle}
            renderInputsProps={renderInputsProps}
            renderFormPassResetHint={renderFormResetHint}
            onSubmit={handleFormSubmit}
        />
    )
}

AuthRegisterPage.displayName = "AuthRegisterPage"

export { AuthRegisterPage }
